import { useEffect, useMemo, useState } from "react";
import { useGlobalContext } from "../../../GlobalContext";
import { Toaster } from "sonner";
import LoadingScreen from "../../Utils/LoadingScreen";
import useVolunteerShopList from "../../../Hooks/useVolunteerShopList";
import {
  RefreshCw,
  Download,
  Search,
  FileDown,
  RefreshCcw,
} from "lucide-react";

export default function VolunteerShopsPage() {
  const { baseurl, accessToken, AdminId, setIsUnauthorised } =
    useGlobalContext();

  const { volunteerShops, loading, fetchVolunteerShops } = useVolunteerShopList(
    {
      baseurl,
      accessToken,
      AdminId,
      setIsUnauthorised,
    },
  );

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 13;

  useEffect(() => {
    fetchVolunteerShops();
  }, []);

  //  SEARCH FILTER
  const filteredData = useMemo(() => {
    return volunteerShops.filter((shop) =>
      Object.values(shop)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase()),
    );
  }, [volunteerShops, search]);

  //  PAGINATION LOGIC
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage]);

  //  REFRESH
  const handleRefresh = () => {
    fetchVolunteerShops();
  };

  //  EXPORT CSV
  const handleExport = () => {
    const headers = [
      "Shop Code",
      "Shop Name",
      "Address",
      "Phone",
      "Latitude",
      "Longitude",
      "Pincode",
      "PAN",
      "GST",
      "Onboarded On",
    ];

    const rows = filteredData.map((shop) => [
      shop.shop_code,
      shop.shop_name,
      shop.shop_address,
      shop.shop_phone,
      shop.shop_latitude,
      shop.shop_longitude,
      shop.shop_pincode,
      shop.shop_pan,
      shop.shop_gst,
      shop.onboardedOn,
    ]);

    let csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows].map((e) => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "volunteer_shops.csv");
    document.body.appendChild(link);
    link.click();
  };

  return (
    <div className="p-6 w-full">
      <Toaster position="top-right" />

      {/* Header Section */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search shops..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9 pr-4 py-2 border rounded-md w-64"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-400 text-white px-4 py-1 rounded shadow"
          >
            <RefreshCcw size={16} />
            <span>Refresh</span>
          </button>

          <button
            onClick={handleExport}
            className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs shadow"
          >
            <FileDown size={14} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          <div className="overflow-x-auto border rounded-md w-full">
            <table className="min-w-full bg-white text-sm">
              <thead className="bg-blue-400 text-white">
                <tr>
                  <th className="px-4 py-2">Shop Code</th>
                  <th className="px-4 py-2">Shop Name</th>
                  <th className="px-4 py-2">Address</th>
                  <th className="px-4 py-2">Phone</th>
                  <th className="px-4 py-2">Latitude</th>
                  <th className="px-4 py-2">Longitude</th>
                  <th className="px-4 py-2">Pincode</th>
                  <th className="px-4 py-2">PAN</th>
                  <th className="px-4 py-2">GST</th>
                  <th className="px-4 py-2">Onboarded On</th>
                </tr>
              </thead>

              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-6 text-gray-500">
                      No shops found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((shop, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-2">{shop.shop_code}</td>
                      <td className="px-4 py-2">{shop.shop_name}</td>
                      <td className="px-4 py-2">{shop.shop_address}</td>
                      <td className="px-4 py-2">{shop.shop_phone}</td>
                      <td className="px-4 py-2">{shop.shop_latitude}</td>
                      <td className="px-4 py-2">{shop.shop_longitude}</td>
                      <td className="px-4 py-2">
                        {shop.shop_pincode || "N/A"}
                      </td>
                      <td className="px-4 py-2">{shop.shop_pan || "N/A"}</td>
                      <td className="px-4 py-2">{shop.shop_gst || "N/A"}</td>
                      <td className="px-4 py-2">
                        {shop.onboardedOn
                          ? new Date(shop.onboardedOn).toLocaleDateString()
                          : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-4 gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>

              <span className="px-4 py-1">
                Page {currentPage} of {totalPages}
              </span>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
